import { Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../component-data-source.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, catchError, tap, timeout, switchMap } from 'rxjs/operators';
import { LoadDataParams } from '../../../interfaces/DataSource/DataSourceParams.interface';
import { UserDTO } from '../../../dto/users/user.dto';
import { User } from '../../../models/user/User.model';
import { environment } from '../../../environment/environment';

export function dual_panel_users(
  componentDataSourceService: ComponentDataSourceService,
  http: HttpClient,
  router: Router
) {
  return {
    name: 'Users for Dual Panel',
    description: 'List of all users optimized for dual panel selector',
    execute: (params?: LoadDataParams) => {
      
      // Check if this is a recursive call
      if (params?.queryParams?.['isRecursiveCall']) {
        return of({ items: [], totalCount: 0 });
      }
      
      // Get current event or path GUID from the URL if available
      const pathSegments = router.url.split('/');
      const eventGuid = pathSegments.find((segment: string) => 
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
      );
      const isUddannelsesvej = pathSegments.includes('uddannelsesvej');
      const uddannelsesvejGuid = isUddannelsesvej ? pathSegments[pathSegments.indexOf('uddannelsesvej') + 1] : null;

      if (eventGuid && !isUddannelsesvej) {
      } else if (isUddannelsesvej && uddannelsesvejGuid) {
      } else {
      }
      
      // Pass all queryParams (including search) to the API for server-side search, just like brugere_status
      let queryParams: any = {};
      if (params?.queryParams) {
        queryParams = { ...params.queryParams };
        
        // Debug log all query params to see what search fields might be present
        
        // Check for ANY parameter that could indicate a search
        const searchKeys = ['search', 'searchQuery', 'q', 'query', 'filter', 'searchText', 'term', 'searchTerm'];
        const hasSearchParam = searchKeys.some(key => key in queryParams);
        
        // Also check if any param value looks like a search string (longer than 2 chars, not a number)
        const hasSearchValue = Object.values(queryParams).some(value => 
          typeof value === 'string' && 
          value.length > 2 && 
          isNaN(Number(value))
        );
        
        // If any search parameter is detected, increase pageSize
        if (hasSearchParam || hasSearchValue) {
          const oldPageSize = queryParams.pageSize;
          queryParams.pageSize = 10000; // Very large value to get all matching users
          queryParams.pageIndex = 0; // Reset to first page for search
        }
      }
      
      // ALWAYS set a large pageSize to ensure all users are fetched, regardless of search
      // This is a temporary fix until we determine why the conditional approach isn't working
      queryParams.pageSize = 10000;
      queryParams.include_path_names = 'true';
      
      
      // Step 1: Get all users first
      return http.get<any>(`${environment.apiUrl}/users`, { params: queryParams }).pipe(
        tap(result => {
        }),
        map(allUsersResult => {
          if (!allUsersResult?.items) {
            console.error('[dual_panel_users] Failed to retrieve users from API');
            return { items: [], totalCount: 0 };
          }
          
          // Deduplicate users by UUID to prevent count issues
          const uniqueUserDTOs = allUsersResult.items.reduce((acc: UserDTO[], userDto: UserDTO) => {
            if (!acc.some(existing => existing.uuid === userDto.uuid)) {
              acc.push(userDto);
            }
            return acc;
          }, []);

          
          // First use User.fromDTO for mapping
          const usersFromDTO = uniqueUserDTOs.map((userDto: UserDTO) => User.fromDTO(userDto));
          
          // Then transform for dual panel format (with value property)
          const allUsers = usersFromDTO.map((user: User, index: number) => {
            const userDto = uniqueUserDTOs[index];
            const pathNamesFromDto = Array.isArray(userDto?.educational_path_names)
              ? userDto.educational_path_names
              : [];
            const pathNamesFromStatus = (user.educational_paths || [])
              .map((path) => path.name || path.full_name || '')
              .filter((name) => !!name);
            const educationalPathNames = Array.from(new Set(
              [...pathNamesFromDto, ...pathNamesFromStatus]
                .map((name) => String(name).trim())
                .filter((name) => name.length > 0)
            ));

            // All locations (primaer, sekundaer, oevrig) — dual panel search only matched primary via `type`
            const locationNames = Array.from(new Set(
              [...(userDto?.locations ?? []), ...(user.locations ?? [])]
                .map((loc: { locations_name?: string }) => String(loc?.locations_name ?? '').trim())
                .filter((name) => name.length > 0)
            ));
            const searchTextParts = [...locationNames, ...educationalPathNames];

            return {
              value: user.uuid,    // Dual panel needs 'value' property for ID
              uuid: user.uuid,     // Keep original property too
              name: user.name,
              type: user.primaryLocation || user.locations?.[0]?.locations_name || '',
              email: user.user,
              searchText: searchTextParts.join(', '),
              status: user.ok_status ? 'OK' : 'Ikke OK',
              user: user          // Keep original user object for reference
            };
          });
          
          // Sort all users by full name
          const sortedAllUsers = [...allUsers].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
          
          // If not an event and not an educational path, return all users
          if (!eventGuid && !uddannelsesvejGuid) {
            return {
              items: sortedAllUsers,
              totalCount: uniqueUserDTOs.length, // Use deduplicated count
              pageSize: allUsersResult.pageSize,
              pageIndex: allUsersResult.pageIndex
            };
          }
          return { allUsers: sortedAllUsers, allUsersResult: { ...allUsersResult, totalCount: uniqueUserDTOs.length } };
        }),
        switchMap((result) => {
          // If we already have items, just return them (no event or path)
          if (result.items) {
            return of(result);
          }
          const { allUsers, allUsersResult } = result;
          
          // Step 2: Check if this is an educational path
          if (isUddannelsesvej && uddannelsesvejGuid) {
            return http.get<any>(`${environment.apiUrl}/paths/${uddannelsesvejGuid}/users`, {
              params: { pageSize: 10000, pageIndex: 0 }
            }).pipe(
              tap(pathUsersResult => {
                const assignedArr = Array.isArray(pathUsersResult?.items) ? pathUsersResult.items : (Array.isArray(pathUsersResult?.data) ? pathUsersResult.data : []);
              }),
              map(pathUsersResult => {
                const assignedArr = Array.isArray(pathUsersResult?.items) ? pathUsersResult.items : (Array.isArray(pathUsersResult?.data) ? pathUsersResult.data : []);
                const assignedUuidSet = new Set<string>(
                  assignedArr
                    .map((up: any) => up?.user?.uuid || up?.uuid || up?.user_uuid)
                    .filter((v: any) => !!v)
                );

                // Merge allUsers and mark assigned as disabled
                const merged = allUsers.map(u => ({
                  ...u,
                  disabled: assignedUuidSet.has(u.uuid)
                }));

                // Keep also a selectedIds list for right panel preselection
                const selectedIds = Array.from(assignedUuidSet);

                return {
                  // Important: put already assigned users into selectedIds, not selectable in available
                  items: merged,
                  totalCount: merged.length,
                  pageSize: allUsersResult.pageSize,
                  pageIndex: allUsersResult.pageIndex,
                  selectedIds,
                  preSelected: true
                };
              }),
              catchError(error => {
                console.error('[dual_panel_users] Error fetching assigned users:', error);
                return of({
                  items: allUsers,
                  totalCount: allUsersResult.totalCount,
                  pageSize: allUsersResult.pageSize,
                  pageIndex: allUsersResult.pageIndex,
                  selectedIds: [],
                  preSelected: false
                });
              })
            );
          }
          
          // Step 3: Get event users (existing logic for events)
          return http.get<any>(`${environment.apiUrl}/events/${eventGuid}`, {
            params: { include_participants: true }
          }).pipe(
            tap(eventResult => {
            }),
            map(eventResult => {
              // Try different possible fields for participants
              const participants = eventResult.participants || 
                                 eventResult.event_participants || 
                                 eventResult.attendees || 
                                 [];
              if (!participants || participants.length === 0) {
                return {
                  items: allUsers,
                  totalCount: allUsersResult.totalCount,
                  pageSize: allUsersResult.pageSize,
                  pageIndex: allUsersResult.pageIndex,
                  selectedIds: [], // Empty array since no users are selected
                  preSelected: false
                };
              }
              // Step 5: Filter out users that are already in the event
              const eventUserIds = new Set(
                participants
                  .filter((p: any) => p.user && p.user.uuid)
                  .map((p: any) => p.user.uuid)
              );
              const availableUsers = allUsers.filter((user: any) => !eventUserIds.has(user.uuid));
              // Sort available users by full name
              const sortedAvailableUsers = [...availableUsers].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
              return {
                items: sortedAvailableUsers,
                totalCount: sortedAvailableUsers.length,
                pageSize: allUsersResult.pageSize,
                pageIndex: allUsersResult.pageIndex,
                selectedIds: Array.from(eventUserIds),
                preSelected: true
              };
            }),
            catchError(error => {
              console.error('[dual_panel_users] Error in event API pipeline:', error);
              return of({
                items: allUsers,
                totalCount: allUsersResult.totalCount,
                pageSize: allUsersResult.pageSize,
                pageIndex: allUsersResult.pageIndex,
                selectedIds: [], // Empty array since we couldn't get event data
                preSelected: false
              });
            })
          );
        }),
        catchError(error => {
          console.error('[dual_panel_users] Error fetching all users:', error);
          return of({ items: [], totalCount: 0 });
        })
      );
    }
  };
} 